import React from 'react';

// Fallback UI constants — MiPAE brand tokens
const STYLES = {
  overlay: {
    minHeight: '100dvh',
    backgroundColor: '#FCF7F3',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '32px 24px',
    fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
  },
  card: {
    maxWidth: 520,
    width: '100%',
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: '48px 40px 40px',
    boxShadow: '0 4px 32px rgba(15,30,60,0.08)',
    borderTop: '4px solid #3C5AAA',
  },
  title: {
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: 'calc(26px * var(--texto-escala, 1))',
    fontWeight: 700,
    color: '#0F1E3C',
    margin: '0 0 20px',
    lineHeight: 1.25,
  },
  body: {
    fontSize: 'calc(15px * var(--texto-escala, 1))',
    color: '#475569',
    lineHeight: 1.75,
    margin: '0 0 28px',
  },
  codeBlock: {
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    padding: '14px 16px',
    marginBottom: 28,
    border: '1px solid #E2E8F0',
  },
  codeLabel: {
    fontSize: 'calc(11px * var(--texto-escala, 1))',
    fontWeight: 600,
    color: '#64748B',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 4,
  },
  codeValue: {
    fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
    fontSize: 'calc(13px * var(--texto-escala, 1))',
    color: '#0F1E3C',
    wordBreak: 'break-all',
    cursor: 'pointer',
    userSelect: 'all',
  },
  actions: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
  },
  btnPrimary: {
    padding: '12px 22px',
    backgroundColor: '#3C5AAA',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 10,
    fontSize: 'calc(14px * var(--texto-escala, 1))',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
  },
  btnSecondary: {
    padding: '12px 22px',
    backgroundColor: 'transparent',
    color: '#3C5AAA',
    border: '1.5px solid #3C5AAA',
    borderRadius: 10,
    fontSize: 'calc(14px * var(--texto-escala, 1))',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
  },
  footer: {
    marginTop: 40,
    textAlign: 'center',
  },
  footerText: {
    fontSize: 'calc(12px * var(--texto-escala, 1))',
    color: '#94A3B8',
  },
};

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, eventId: null };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, { componentStack }) {
    if (process.env.NODE_ENV === 'production' && window.Sentry) {
      try {
        const sessionId = sessionStorage.getItem('mipae:session_id');
        if (sessionId) window.Sentry.setTag('session_id', sessionId);
      } catch (e) {}
      const eventId = window.Sentry.captureException(error, {
        contexts: { react: { componentStack } },
      });
      this.setState({ eventId });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const eventId = this.state.eventId || null;
    const sessionId = (() => {
      try { return sessionStorage.getItem('mipae:session_id') || null; } catch { return null; }
    })();

    return (
      <div style={STYLES.overlay}>
        <div style={STYLES.card}>
          <h1 style={STYLES.title}>Algo se nos salió de las manos</h1>
          <p style={STYLES.body}>
            Tuvimos un error inesperado y ya quedó registrado. Si vuelve a pasar,
            puedes escribirnos con este código para que lo revisemos.
          </p>

          {(eventId || sessionId) && (
            <div style={STYLES.codeBlock}>
              {eventId && (
                <div style={{ marginBottom: sessionId ? 12 : 0 }}>
                  <div style={STYLES.codeLabel}>Código de error (Sentry)</div>
                  <div
                    style={STYLES.codeValue}
                    title="Clic para seleccionar"
                  >
                    {eventId}
                  </div>
                </div>
              )}
              {sessionId && (
                <div>
                  <div style={STYLES.codeLabel}>ID de sesión</div>
                  <div
                    style={STYLES.codeValue}
                    title="Clic para seleccionar"
                  >
                    {sessionId}
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={STYLES.actions}>
            <button
              style={STYLES.btnPrimary}
              onClick={() => { window.location.href = '/'; }}
            >
              Volver al inicio
            </button>
            <button
              style={STYLES.btnSecondary}
              onClick={() => this.setState({ hasError: false, eventId: null })}
            >
              Reintentar
            </button>
          </div>
        </div>

        <div style={STYLES.footer}>
          <img
            src="/brand/mipae-lockup.png"
            alt="MiPAE"
            style={{ height: 28, opacity: 0.5 }}
          />
        </div>
      </div>
    );
  }
}
